<template>
  <!-- 仅当拥有多家公司权限（非纯老师）时才展示切换入口；单公司/纯老师完全不显示公司名 -->
  <div class="company-switcher" v-if="canSwitch">
    <van-tag plain type="primary" size="large" @click="showPicker = canSwitch">
      {{ currentCompanyName }}
      <van-icon v-if="canSwitch" name="arrow-down" size="12" style="margin-left: 3px" />
    </van-tag>

    <van-popup v-model:show="showPicker" position="bottom" round teleport="body">
      <van-picker
        :columns="companyColumns"
        title="选择公司"
        @confirm="onConfirm"
        @cancel="showPicker = false"
      />
    </van-popup>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useDict, selectDictLabel } from '@/utils/dict'
import { getUserInfo } from '@/utils/authUtils'
import { BOERSI_COMPANY_ID, BOSI_COMPANY_ID, initializeCompanyContext, setCurrentCompanyId } from '@/utils/company'

const props = defineProps({
  companyIds: { type: Array, default: () => [] }
})

const { company_list } = useDict('company_list')
let userInfo = null
try {
  userInfo = getUserInfo()
} catch (e) {
  userInfo = null
}
const currentCompanyId = ref(initializeCompanyContext(userInfo))
const showPicker = ref(false)

// 弹窗弹出时隐藏底部 tabbar，关闭时恢复，避免遮挡（与签名弹窗一致）
watch(showPicker, (val) => {
  if (val) {
    hideTabbar()
  } else {
    restoreTabbar()
  }
})

onBeforeUnmount(() => {
  restoreTabbar()
})

function hideTabbar() {
  const tabbar = document.querySelector('.van-tabbar')
  if (tabbar) {
    tabbar.dataset.prevDisplay = tabbar.style.display || ''
    tabbar.style.display = 'none'
  }
}

function restoreTabbar() {
  const tabbar = document.querySelector('.van-tabbar')
  if (tabbar) {
    tabbar.style.display = tabbar.dataset.prevDisplay || ''
    delete tabbar.dataset.prevDisplay
  }
}

watch(
  () => props.companyIds,
  (companyIds) => {
    // 父页面加载前不重置已缓存的公司，避免公司上下文闪回默认值
    if (Array.isArray(companyIds) && companyIds.length > 0) {
      currentCompanyId.value = initializeCompanyContext({ companyIds })
    }
  },
  { immediate: true }
)

const availableCompanyIds = computed(() => {
  const ids = Array.isArray(props.companyIds) && props.companyIds.length > 0
    ? props.companyIds
    : (Array.isArray(userInfo?.companyIds) ? userInfo.companyIds : [])
  const companyIds = ids.map(Number).filter(id => [1, 2].includes(id))
  // 超级管理员未配置公司权限时表示可见全部公司，H5 不提供“全部公司”，允许在两家公司间切换
  return companyIds.length > 0 ? companyIds : [BOERSI_COMPANY_ID, BOSI_COMPANY_ID]
})

// 是否纯老师角色（只有 teacher 角色，禁用公司切换）
const isOnlyTeacher = computed(() => {
  const roles = userInfo?.roles || []
  return roles.length === 1 && roles[0] === 'teacher'
})

const canSwitch = computed(() => availableCompanyIds.value.length > 1 && !isOnlyTeacher.value)
const companyColumns = computed(() =>
  (company_list.value || [])
    .filter(item => availableCompanyIds.value.includes(Number(item.dictValue)))
    .map(item => ({ text: item.dictLabel, value: Number(item.dictValue) }))
)
const currentCompanyName = computed(() => selectDictLabel(company_list.value, currentCompanyId.value))

function onConfirm({ selectedOptions }) {
  const companyId = selectedOptions[0]?.value
  if (!companyId || companyId === currentCompanyId.value) {
    showPicker.value = false
    return
  }
  setCurrentCompanyId(companyId)
  showPicker.value = false
  // 与 PC 端保持一致：切换公司后整页刷新，避免缓存数据串公司
  window.location.reload()
}
</script>

<style scoped>
.company-switcher {
  display: inline-flex;
  align-items: center;
}
</style>
